// src/components/MaintanceToDoContainer.jsx
import { theme, Grid } from 'antd';
import React, { useMemo } from 'react';
import MaintanceToDoItemList from './MaintanceToDoItemList';
import { useMaintanceToDo } from 'providers/MaintanceToDoProvider';

const { useToken } = theme;

// Recibe TODOS los ascensores + cuál está activo para poder pintar el segmented.
function MaintanceToDoContainer({
  ascensores,
  activeAscensorId,
  setActiveAscensorId,
  ascensorStats,
}) {
  const { token } = useToken();
  const screens = Grid.useBreakpoint();
  const isDesktop = screens.lg;
  const { onChangeEstado, estados } = useMaintanceToDo();

  const activeAscensor = useMemo(
    () => (ascensores || []).find((a) => a.id === activeAscensorId) || ascensores?.[0],
    [ascensores, activeAscensorId]
  );
  const actividades = activeAscensor?.ActividadRespuestas;

  const categorias = useMemo(() => {
    const grouped = actividades?.reduce((acc, obj) => {
      const { Actividad: ActividadData, ...Respuesta } = obj;
      const { Categoria, ...Actividad } = ActividadData;
      const { nombre } = Categoria;
      if (!acc[nombre]) acc[nombre] = { ...Categoria, actividades: [] };
      acc[nombre].actividades.push({ Respuesta, Actividad });
      return acc;
    }, {});
    return Object.values(grouped || {});
  }, [actividades]);

  // Progreso global del ascensor activo (cuenta overrides del provider).
  const totals = useMemo(() => {
    let total = 0,
      done = 0,
      alert = 0;
    categorias.forEach((cat) =>
      cat.actividades.forEach(({ Respuesta }) => {
        total += 1;
        const eff =
          estados?.[Respuesta.id] !== undefined ? estados[Respuesta.id] : Respuesta.estado;
        if (eff) done += 1;
        if (eff === 'RECHAZADO') alert += 1;
      })
    );
    return { total, done, alert };
  }, [categorias, estados]);

  const pct = totals.total ? (totals.done / totals.total) * 100 : 0;

  // Selector de ascensores (segmented). Solo si hay más de uno.
  const selectorNode = ascensores?.length > 1 && (
    <div style={{ display: 'flex', padding: isDesktop ? 0 : '8px 0 0' }}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          gap: 3,
          padding: 3,
          background: token.colorFillTertiary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
        }}
      >
        {ascensores.map((a) => {
          const on = a.id === activeAscensorId;
          const st = ascensorStats?.[a.id] || {};
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setActiveAscensorId(a.id)}
              style={{
                flex: 1,
                border: 0,
                background: on ? token.colorBgContainer : 'transparent',
                color: on ? token.colorText : token.colorTextSecondary,
                boxShadow: on ? token.boxShadowTertiary : 'none',
                fontWeight: 600,
                fontSize: 12.5,
                padding: '7px 6px',
                borderRadius: token.borderRadius,
                cursor: 'pointer',
              }}
            >
              {(a.nombre || 'Ascensor').replace('Ascensor ', '')}
              {st.problemasIncompletos > 0 && (
                <span className="mono" style={{ marginLeft: 6, color: token.colorError }}>
                  {st.problemasIncompletos}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Barra de progreso global del ascensor activo.
  const progresoNode = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: isDesktop ? 0 : '10px 2px 8px',
      }}
    >
      <span className="mono" style={{ fontSize: 11.5, color: token.colorTextSecondary }}>
        <b style={{ color: token.colorText }}>{totals.done}</b>/{totals.total}
      </span>
      <div
        style={{
          flex: 1,
          height: 7,
          borderRadius: 999,
          background: token.colorFillTertiary,
          border: `1px solid ${token.colorBorderSecondary}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: token.colorPrimary,
            transition: 'width .35s cubic-bezier(.4,0,.2,1)',
          }}
        />
      </div>
      <span
        className="mono"
        style={{ fontSize: 11.5, color: totals.alert ? token.colorError : token.colorSuccess }}
      >
        {totals.alert} ⚠
      </span>
    </div>
  );

  // Chips de categoría con salto (scroll-to). En desktop se apilan vertical
  // dentro del panel de contexto; en mobile es una fila con scroll horizontal.
  const seccionesNode = (
    <div
      style={{
        display: 'flex',
        flexDirection: isDesktop ? 'column' : 'row',
        gap: 7,
        padding: isDesktop ? 0 : '2px 0 11px',
        overflowX: isDesktop ? 'visible' : 'auto',
      }}
    >
      {categorias.map((c, i) => {
        let d = 0,
          al = 0;
        c.actividades.forEach(({ Respuesta }) => {
          const eff =
            estados?.[Respuesta.id] !== undefined ? estados[Respuesta.id] : Respuesta.estado;
          if (eff) d += 1;
          if (eff === 'RECHAZADO') al += 1;
        });
        const done = d === c.actividades.length && c.actividades.length > 0;
        const color =
          al > 0 ? token.colorError : done ? token.colorSuccess : token.colorTextSecondary;
        const border = al > 0 ? token.colorError : done ? token.colorSuccess : token.colorBorder;
        return (
          <button
            key={i}
            type="button"
            onClick={() => {
              const el = document.getElementById(`cat-${i}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isDesktop ? 'space-between' : 'center',
              gap: 7,
              padding: '6px 10px',
              borderRadius: isDesktop ? token.borderRadius : 999,
              border: `1px solid ${border}`,
              background: token.colorBgContainer,
              color,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {isDesktop ? c.nombre.split(' / ')[0] : c.nombre.split(' / ')[0].split(' ')[0]}
            <span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>
              {d}/{c.actividades.length}
            </span>
          </button>
        );
      })}
    </div>
  );

  // Acordeones de categoría (la lista de actividades, con sus id="cat-i").
  const acordeonesNode = (
    <div style={{ marginTop: isDesktop ? 0 : 10 }}>
      {categorias.map((item, index) => (
        <MaintanceToDoItemList
          key={index}
          id={`cat-${index}`}
          item={item}
          index={index}
          estados={estados}
          onChangeEstado={onChangeEstado}
          idAscensor={activeAscensorId}
        />
      ))}
    </div>
  );

  // Desktop: dos columnas — lista a la izquierda, panel de contexto sticky a la
  // derecha (selector + progreso + secciones migrados del subhead mobile).
  if (isDesktop) {
    const microLabel = (text) => (
      <div
        style={{
          fontSize: token.fontSizeSM,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: token.colorTextTertiary,
          marginBottom: 8,
        }}
      >
        {text}
      </div>
    );
    return (
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: 820 }}>{acordeonesNode}</div>
        <aside
          style={{
            flex: '0 0 320px',
            position: 'sticky',
            top: 80,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: 16,
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusLG,
          }}
        >
          {selectorNode && (
            <div
              style={{
                paddingBottom: 16,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              {selectorNode}
            </div>
          )}
          <div>
            {microLabel('Progreso')}
            {progresoNode}
          </div>
          <div>
            {microLabel('Secciones')}
            {seccionesNode}
          </div>
        </aside>
      </div>
    );
  }

  // Mobile: subhead sticky inline (comportamiento original) + acordeones.
  return (
    <div>
      <div
        style={{
          position: 'sticky',
          // El header del LayoutMain es fixed y mide 64px (h-16); el subhead
          // se pega justo debajo para no quedar tapado al scrollear.
          top: 64,
          zIndex: 20,
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          margin: '0 -4px',
          padding: '0 4px',
        }}
      >
        {selectorNode}
        {progresoNode}
        {seccionesNode}
      </div>
      {acordeonesNode}
    </div>
  );
}

export default MaintanceToDoContainer;
